import { WSEmptyState, WSList } from "@wingspanhq/fe-component-library";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useUserId } from "../../../../query/hooks/helpers";
import { useViewNotification } from "../../../../query/notifications/mutations/useViewNotification";
import { useMemberProfile } from "../../../../query/users/queries";
import { selectorHasPayerContextUser } from "../../../../shared/selectors/selectorHasPayerContextUser";
import { selectorIsEnterpriseUser } from "../../../../shared/selectors/selectorIsEnterpriseUser";
import { openInNewTab } from "../../../../shared/utils/openInNewTab";
import { useTrackClickNotification } from "../../utils/useTrackClickNotification";
import {
  getNotificationTemplate,
  NotificationTemplateParameters,
  WSNotificationResponse
} from "../Notifications";
import { Promotions, useShowPromotions } from "../Promotions";
import { TodoItem } from "../TodoItem";
import { selectorGroupedTodos } from "../../selectors/selectorGroupedTodos";
import { selectorPayerLogoFileIdFromContext } from "../../selectors/selectorPayerLogoFileIdFromContext";
import { selectorPayerNameFromContext } from "../../selectors/selectorPayerNameFromContext";

type Props = {
  notifications: WSNotificationResponse[];
};

export const TodoList: React.FC<Props> = props => {
  const userId = useUserId();
  const queryMember = useMemberProfile(userId);
  const history = useHistory();

  const isPayerView =
    queryMember.data &&
    (selectorIsEnterpriseUser(queryMember.data.user) ||
      selectorHasPayerContextUser(queryMember.data.user));

  const todoGroups = useMemo(
    () => selectorGroupedTodos(props.notifications),
    [props.notifications]
  );

  const trackClickNotification = useTrackClickNotification(userId);
  const [readNotification] = useViewNotification();

  const showPromotions = useShowPromotions();

  return (
    <WSList>
      {todoGroups.length === 0 && !showPromotions ? (
        <WSEmptyState
          type="tasks"
          title="You’re all set for now"
          description={
            <>
              New tasks will appear here as they arise.
              <br />
              Have a great day!.
            </>
          }
        />
      ) : (
        <>
          {todoGroups.map(({ group, ...notification }) => {
            const taskPreset = getNotificationTemplate(notification.handle);

            const Button = taskPreset?.Footer;
            const Header = taskPreset?.Header;
            const taskProps = {
              ...notification,
              group
            } as NotificationTemplateParameters;
            const hasGroups = group.length > 1;

            const logoFileId = selectorPayerLogoFileIdFromContext(
              notification.context
            );
            const companyName = selectorPayerNameFromContext(
              notification.context
            );

            return (
              <TodoItem
                key={notification.channelMessageId}
                isPending={taskPreset?.isPending}
                onClick={() => {
                  if (group.length === 1) {
                    trackClickNotification(notification);
                  }
                }}
                viewed={group.every(data => data.isViewed)}
                body={
                  taskPreset
                    ? taskPreset.renderBody(taskProps, history)
                    : notification.message
                }
                buttonAction={
                  taskPreset
                    ? taskPreset.buttonAction?.(taskProps, history)
                    : notification.actionURL
                    ? () => {
                        const { hostname, pathname, search, hash } = new URL(
                          notification.actionURL!
                        );

                        if (hostname !== window.location.hostname) {
                          openInNewTab(notification.actionURL!);
                          return;
                        }

                        history.push(pathname + search + hash);
                      }
                    : undefined
                }
                buttonActionAsync={
                  taskPreset
                    ? taskPreset.buttonActionAsync?.(taskProps, history)
                    : undefined
                }
                title={
                  taskPreset
                    ? taskPreset.renderTitle(taskProps)
                    : notification.subject
                }
                status={notification.status}
                footer={Button ? <Button {...taskProps} /> : null}
                header={Header ? <Header {...taskProps} /> : null}
                buttonText={
                  taskPreset
                    ? taskPreset.buttonText?.(taskProps)
                    : notification.actionTitle
                }
                numberOfTasks={hasGroups ? group.length : undefined}
                company={
                  logoFileId && companyName && !hasGroups
                    ? {
                        logoFileId,
                        name: companyName
                      }
                    : undefined
                }
                onRead={
                  group.some(data => !data.isViewed)
                    ? () => {
                        group.forEach(data => {
                          if (!data.isViewed) {
                            readNotification(data.channelMessageId);
                          }
                        });
                      }
                    : undefined
                }
              />
            );
          })}

          <Promotions />
        </>
      )}
    </WSList>
  );
};
